import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ExpandableTextProps {
  text: string;
  maxHeight?: number;
  className?: string;
}

export function ExpandableText({ text, maxHeight = 120, className = '' }: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [fullHeight, setFullHeight] = useState<number | undefined>(undefined);
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setFullHeight(node.scrollHeight);
      setNeedsTruncation(node.scrollHeight > maxHeight);
    }
  }, [maxHeight]);
  const observerRef = useRef<HTMLDivElement | null>(null);

  // Re-measure if text changes
  useEffect(() => {
    if (observerRef.current) {
      setFullHeight(observerRef.current.scrollHeight);
      setNeedsTruncation(observerRef.current.scrollHeight > maxHeight);
    }
  }, [text, maxHeight]);

  const setRefs = useCallback((node: HTMLDivElement | null) => {
    observerRef.current = node;
    contentRef(node);
  }, [contentRef]);

  const computedMaxHeight = expanded || !needsTruncation ? fullHeight ?? 'none' : maxHeight;

  return (
    <div>
      <div
        ref={setRefs}
        className={`overflow-hidden transition-[max-height] duration-300 ${className}`}
        style={{ maxHeight: computedMaxHeight }}
      >
        {text}
      </div>
      {needsTruncation && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer"
        >
          {expanded ? (
            <>Show less <ChevronUp className="w-4 h-4" /></>
          ) : (
            <>Show more <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
      )}
    </div>
  );
}
